import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  MAX_BOOTSTRAP_ADMINS,
  invalidEmails,
  parseEmails,
  tenantAdminsErrorMessage,
  useBootstrapTenantAdmins,
  useTenantAdmins,
} from "@/hooks/use-tenant-admins";
import type { TenantAdmin } from "@/hooks/use-tenant-admins";

interface TenantAdminsSectionProps {
  tenantId: string;
  tenantName: string;
}

function AdminRow({ admin }: { admin: TenantAdmin }) {
  return (
    <li className="py-2">
      <div className="truncate text-sm font-medium">{admin.name}</div>
      <div className="text-muted-foreground truncate text-xs">
        {admin.email}
      </div>
    </li>
  );
}

export function TenantAdminsSection({
  tenantId,
  tenantName,
}: TenantAdminsSectionProps) {
  const { data: group, isLoading, error } = useTenantAdmins(tenantId);
  const bootstrap = useBootstrapTenantAdmins();
  const [raw, setRaw] = useState("");
  const [confirming, setConfirming] = useState(false);

  const currentEmails = useMemo(
    () => (group?.admins ?? []).map((admin) => admin.email),
    [group],
  );
  // The form starts from the current roster, so a re-run reads as editing
  // the list rather than as adding to it
  useEffect(() => {
    setRaw(currentEmails.join("\n"));
  }, [currentEmails]);

  const emails = useMemo(() => parseEmails(raw), [raw]);
  const invalid = useMemo(() => invalidEmails(emails), [emails]);
  const added = emails.filter((email) => !currentEmails.includes(email));
  const removed = currentEmails.filter((email) => !emails.includes(email));
  const unchanged =
    group !== null && added.length === 0 && removed.length === 0;

  const inputError =
    invalid.length > 0
      ? `Not an email address: ${invalid.join(", ")}`
      : emails.length > MAX_BOOTSTRAP_ADMINS
        ? `At most ${String(MAX_BOOTSTRAP_ADMINS)} admins at a time`
        : null;
  const canSubmit =
    emails.length > 0 &&
    inputError === null &&
    !unchanged &&
    !bootstrap.isPending &&
    !isLoading;

  const handleConfirm = async () => {
    setConfirming(false);
    try {
      const result = await bootstrap.mutateAsync({
        tenant_id: tenantId,
        emails,
      });
      toast.success(
        result.group_created
          ? `${result.group_name} created with ${String(result.admins.length)} admin(s)`
          : `${result.group_name} now has ${String(result.admins.length)} admin(s)`,
      );
    } catch (err: unknown) {
      toast.error(
        tenantAdminsErrorMessage(err, "The admins could not be set up"),
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-5" />
          Harmony Admins
          {group ? (
            <Badge variant="secondary" className="ml-auto text-xs">
              {group.admins.length}
            </Badge>
          ) : null}
        </CardTitle>
        <CardDescription>
          The people who get Platform Admin in {tenantName} from their first SSO
          login, with no Descope role involved. Only for tenants on permissions
          v2.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-5 w-64" />
          </div>
        ) : error ? (
          <p role="alert" className="text-destructive text-sm">
            {tenantAdminsErrorMessage(error, "Could not load the admins group")}
          </p>
        ) : group === null || group === undefined ? (
          <p className="text-muted-foreground text-sm">
            Not bootstrapped yet: this tenant has no Harmony Admins group.
          </p>
        ) : (
          <div className="space-y-2">
            {!group.has_platform_admin ? (
              <div
                role="status"
                className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
              >
                <AlertTriangle aria-hidden="true" className="mt-0.5 size-4" />
                <span>
                  The group no longer carries Platform Admin. Someone removed it
                  in the product; running the bootstrap again restores it.
                </span>
              </div>
            ) : null}
            {group.admins.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                The group {group.group_name} exists but has no members.
              </p>
            ) : (
              <ul className="divide-y">
                {group.admins.map((admin) => (
                  <AdminRow key={admin.employee_id} admin={admin} />
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="tenant-admin-emails">
            {group ? "Admins (replaces the list above)" : "Admin emails"}
          </Label>
          <Textarea
            id="tenant-admin-emails"
            value={raw}
            rows={4}
            placeholder={"dana@customer.com\nomer@customer.com"}
            disabled={bootstrap.isPending || isLoading}
            onChange={(event) => {
              setRaw(event.target.value);
            }}
            aria-invalid={inputError !== null}
            aria-describedby={
              inputError
                ? "tenant-admin-emails-error"
                : "tenant-admin-emails-hint"
            }
          />
          {inputError ? (
            <p
              id="tenant-admin-emails-error"
              role="alert"
              className="text-destructive text-xs"
            >
              {inputError}
            </p>
          ) : (
            <p
              id="tenant-admin-emails-hint"
              className="text-muted-foreground text-xs"
            >
              One per line. Each must be exactly the email the customer&apos;s
              IdP asserts at SSO login: an alias or another domain will not
              match, and the person would sign in without admin access.
            </p>
          )}
        </div>

        <Button
          onClick={() => {
            setConfirming(true);
          }}
          disabled={!canSubmit}
        >
          {bootstrap.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : null}
          {group ? "Update admins" : "Bootstrap admins"}
        </Button>
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {group ? "Update" : "Bootstrap"} Harmony Admins for {tenantName}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {added.length > 0 ? (
                  <div>
                    <div className="font-medium">
                      Gets Platform Admin ({added.length})
                    </div>
                    <ul className="text-muted-foreground list-inside list-disc">
                      {added.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {removed.length > 0 ? (
                  <div>
                    <div className="text-destructive font-medium">
                      Loses Platform Admin ({removed.length})
                    </div>
                    <ul className="text-muted-foreground list-inside list-disc">
                      {removed.map((email) => (
                        <li key={email}>{email}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {group && !group.has_platform_admin ? (
                  <p>The Platform Admin grant will be restored on the group.</p>
                ) : null}
                <p>
                  People who have not signed in yet get access from their first
                  SSO login. Removals take effect on the person&apos;s next
                  request.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleConfirm();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
